import { useState } from "react";
import { mxn } from "@/lib/api";
import { Modal, Btn, Input } from "@/components/Kit";
import { Plus, Trash2, PackagePlus, ChevronDown } from "lucide-react";

export function LineItemsEditor({ items, setItems, ivaRate, setIvaRate, products = [], onCreateProduct }) {
  const [prodOpen, setProdOpen] = useState(false);
  const [pForm, setPForm] = useState({ name: "", unit: "pieza", unit_price: 0, sku: "" });

  const update = (idx, key, val) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };
  const add = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, product_id: null }]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

  const addFromCatalog = (pid) => {
    if (!pid) return;
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setItems([...items, { description: p.name, quantity: 1, unit_price: p.unit_price, product_id: p.id }]);
  };

  const createProduct = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const created = await onCreateProduct({ ...pForm, unit_price: Number(pForm.unit_price) || 0, active: true });
    if (created) {
      setItems([...items, { description: created.name, quantity: 1, unit_price: created.unit_price, product_id: created.id }]);
    }
    setProdOpen(false);
    setPForm({ name: "", unit: "pieza", unit_price: 0, sku: "" });
  };

  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const iva = subtotal * (Number(ivaRate) / 100);
  const total = subtotal + iva;
  const active = products.filter((p) => p.active !== false);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">Conceptos</div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value=""
              data-testid="catalog-select"
              onChange={(e) => addFromCatalog(e.target.value)}
              className="appearance-none bg-white border border-[#E3E8EE] pl-3 pr-7 py-1.5 font-mono-data text-xs focus:outline-none focus:ring-1 focus:ring-[#465B7F] cursor-pointer"
            >
              <option value="">+ Del catálogo</option>
              {active.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {mxn(p.unit_price)}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#5B6672]" />
          </div>
          <button
            type="button"
            onClick={() => setProdOpen(true)}
            data-testid="new-product-inline-button"
            className="inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors"
          >
            <PackagePlus size={13} /> Nuevo producto
          </button>
        </div>
      </div>

      <div className="border border-[#E3E8EE]">
        <div className="grid grid-cols-12 gap-2 bg-[#465B7F] px-3 py-2.5 text-xs uppercase tracking-widest font-bold text-white">
          <div className="col-span-6">Descripción</div>
          <div className="col-span-2 text-right">Cant.</div>
          <div className="col-span-2 text-right">P. Unit.</div>
          <div className="col-span-2 text-right">Importe</div>
        </div>
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-start px-3 py-2 border-t border-[#E3E8EE]">
            <textarea
              rows={2}
              className="col-span-6 bg-white border border-[#E3E8EE] font-mono-data text-sm px-2 py-1 leading-snug resize-y min-h-[38px] focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
              placeholder="Servicio / concepto"
              value={it.description}
              data-testid={`item-desc-${idx}`}
              onChange={(e) => update(idx, "description", e.target.value)}
            />
            <input
              type="number" step="any"
              className="col-span-2 bg-transparent font-mono-data text-sm py-1 text-right focus:outline-none border-b border-transparent focus:border-[#465B7F]"
              value={it.quantity}
              data-testid={`item-qty-${idx}`}
              onChange={(e) => update(idx, "quantity", parseFloat(e.target.value) || 0)}
            />
            <input
              type="number" step="any"
              className="col-span-2 bg-transparent font-mono-data text-sm py-1 text-right focus:outline-none border-b border-transparent focus:border-[#465B7F]"
              value={it.unit_price}
              data-testid={`item-price-${idx}`}
              onChange={(e) => update(idx, "unit_price", parseFloat(e.target.value) || 0)}
            />
            <div className="col-span-2 flex items-center justify-end gap-2">
              <span className="font-mono-data text-sm">
                {mxn((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
              </span>
              <button type="button" onClick={() => remove(idx)} className="text-[#5B6672] hover:text-[#ED1F26]">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          data-testid="add-item-button"
          className="flex items-center gap-2 px-3 py-2 border-t border-[#E3E8EE] text-sm text-[#465B7F] hover:bg-[#465B7F]/5 w-full transition-colors"
        >
          <Plus size={14} /> Agregar concepto
        </button>
      </div>

      <div className="flex justify-end mt-4">
        <div className="w-64 space-y-2">
          <Row label="Subtotal" value={mxn(subtotal)} />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="overline">IVA</span>
              <input
                type="number" step="any"
                value={ivaRate}
                data-testid="iva-rate-input"
                onChange={(e) => setIvaRate(parseFloat(e.target.value) || 0)}
                className="w-14 bg-white border border-[#E3E8EE] px-2 py-0.5 font-mono-data text-xs text-right focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
              />
              <span className="text-xs text-[#5B6672]">%</span>
            </div>
            <span className="font-mono-data text-sm" data-testid="iva-amount">{mxn(iva)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-[#16202B] pt-2">
            <span className="overline text-[#16202B]">Total</span>
            <span className="font-mono-data text-lg font-semibold text-[#465B7F]" data-testid="total-amount">
              {mxn(total)}
            </span>
          </div>
        </div>
      </div>

      <Modal open={prodOpen} onClose={() => setProdOpen(false)} title="Nuevo producto">
        <form onSubmit={createProduct} className="space-y-4">
          <Input label="Nombre *" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} required testid="quick-product-name" />
          <div className="grid grid-cols-3 gap-4">
            <Input label="SKU" value={pForm.sku} onChange={(e) => setPForm({ ...pForm, sku: e.target.value })} testid="quick-product-sku" />
            <Input label="Unidad" value={pForm.unit} onChange={(e) => setPForm({ ...pForm, unit: e.target.value })} testid="quick-product-unit" />
            <Input label="Precio *" type="number" step="any" value={pForm.unit_price} onChange={(e) => setPForm({ ...pForm, unit_price: e.target.value })} required testid="quick-product-price" />
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setProdOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-quick-product-button">Crear y agregar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="overline">{label}</span>
      <span className="font-mono-data text-sm">{value}</span>
    </div>
  );
}
